import { useMemo } from 'react';
import { BarChart3, TrendingUp, Heart, Calendar, Zap, Star } from 'lucide-react';
import { Card } from '../common';
import { spreads } from '../../data/spreads';
import type { TarotReading } from '../../types/tarot';

interface ReadingAnalyticsProps {
  readings: TarotReading[];
}

interface AnalyticsData {
  totalReadings: number;
  favoriteReadings: number;
  privateReadings: number;
  completedReadings: number;
  averageReadingsPerMonth: number;
  mostUsedSpread: { name: string; count: number };
  mostUsedTags: { tag: string; count: number }[];
  readingStreak: number;
  monthlyTrend: { month: string; count: number }[];
  spreadDistribution: { name: string; count: number; percentage: number }[];
}

export default function ReadingAnalytics({ readings }: ReadingAnalyticsProps) {
  const analytics = useMemo((): AnalyticsData => {
    if (readings.length === 0) {
      return {
        totalReadings: 0,
        favoriteReadings: 0,
        privateReadings: 0,
        completedReadings: 0,
        averageReadingsPerMonth: 0,
        mostUsedSpread: { name: 'None', count: 0 },
        mostUsedTags: [],
        readingStreak: 0,
        monthlyTrend: [],
        spreadDistribution: []
      };
    }

    // Basic counts
    const totalReadings = readings.length;
    const favoriteReadings = readings.filter(r => r.isFavorite).length;
    const privateReadings = readings.filter(r => r.isPrivate).length;
    const completedReadings = readings.filter(r => r.status === 'completed').length;

    // Spread analysis
    const spreadCounts = readings.reduce((acc, reading) => {
      acc[reading.spreadId] = (acc[reading.spreadId] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const mostUsedSpreadId = Object.entries(spreadCounts)
      .sort(([, a], [, b]) => b - a)[0];
    
    const mostUsedSpread = mostUsedSpreadId 
      ? {
          name: spreads.find(s => s.id === mostUsedSpreadId[0])?.name || 'Unknown',
          count: mostUsedSpreadId[1]
        }
      : { name: 'None', count: 0 };

    const spreadDistribution = Object.entries(spreadCounts)
      .map(([spreadId, count]) => ({
        name: spreads.find(s => s.id === spreadId)?.name || 'Unknown',
        count,
        percentage: Math.round((count / totalReadings) * 100)
      }))
      .sort((a, b) => b.count - a.count);

    // Tag analysis
    const tagCounts = readings.reduce((acc, reading) => {
      reading.tags.forEach(tag => {
        acc[tag] = (acc[tag] || 0) + 1;
      });
      return acc;
    }, {} as Record<string, number>);

    const mostUsedTags = Object.entries(tagCounts)
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    // Time-based analysis
    const sortedReadings = [...readings].sort((a, b) => 
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );

    // Monthly trend (last 6 months)
    const now = new Date();
    const monthlyTrend = [];
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
      const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      
      const count = readings.filter(r => {
        const readingDate = new Date(r.createdAt);
        return readingDate >= monthStart && readingDate <= monthEnd;
      }).length;

      monthlyTrend.push({
        month: date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        count
      });
    }

    // Average readings per month
    const firstReading = sortedReadings[0];
    const monthsSinceFirst = firstReading 
      ? Math.max(1, Math.ceil((now.getTime() - new Date(firstReading.createdAt).getTime()) / (1000 * 60 * 60 * 24 * 30)))
      : 1;
    const averageReadingsPerMonth = Math.round(totalReadings / monthsSinceFirst * 10) / 10;

    // Reading streak (consecutive days with readings in the last 30 days)
    let readingStreak = 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let i = 0; i < 30; i++) {
      const checkDate = new Date(today);
      checkDate.setDate(checkDate.getDate() - i);
      
      const hasReadingOnDate = readings.some(r => {
        const readingDate = new Date(r.createdAt);
        readingDate.setHours(0, 0, 0, 0);
        return readingDate.getTime() === checkDate.getTime();
      });
      
      if (hasReadingOnDate) {
        readingStreak++;
      } else if (i > 0) { // Don't break on first day (today) if no reading
        break;
      }
    }

    return {
      totalReadings,
      favoriteReadings,
      privateReadings,
      completedReadings,
      averageReadingsPerMonth,
      mostUsedSpread,
      mostUsedTags,
      readingStreak,
      monthlyTrend,
      spreadDistribution
    };
  }, [readings]);

  if (readings.length === 0) {
    return (
      <Card className="text-center py-8">
        <BarChart3 className="h-12 w-12 text-gray-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-white mb-2">No Analytics Yet</h3>
        <p className="text-gray-400">
          Start creating readings to see your insights and patterns
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="text-center">
          <div className="text-2xl font-bold text-primary-500 mb-1">
            {analytics.totalReadings}
          </div>
          <div className="text-sm text-gray-400">Total Readings</div>
        </Card>
        
        <Card className="text-center">
          <div className="text-2xl font-bold text-red-500 mb-1">
            {analytics.favoriteReadings}
          </div>
          <div className="text-sm text-gray-400">Favorites</div>
        </Card>
        
        <Card className="text-center">
          <div className="text-2xl font-bold text-green-500 mb-1">
            {analytics.completedReadings}
          </div>
          <div className="text-sm text-gray-400">Completed</div>
        </Card>
        
        <Card className="text-center">
          <div className="text-2xl font-bold text-purple-500 mb-1">
            {analytics.readingStreak}
          </div>
          <div className="text-sm text-gray-400">Day Streak</div>
        </Card>
      </div>

      {/* Detailed Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Trend */}
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-5 w-5 text-primary-500" />
            <h3 className="font-semibold text-white">Monthly Activity</h3>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-400">Average per month:</span>
              <span className="text-white font-medium">{analytics.averageReadingsPerMonth}</span>
            </div>
            
            <div className="space-y-2">
              {analytics.monthlyTrend.map((month, index) => (
                <div key={index} className="flex items-center gap-3">
                  <span className="text-xs text-gray-400 w-12">{month.month}</span>
                  <div className="flex-1 bg-dark-700 rounded-full h-2">
                    <div 
                      className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${Math.max(5, (month.count / Math.max(...analytics.monthlyTrend.map(m => m.count))) * 100)}%` 
                      }}
                    />
                  </div>
                  <span className="text-xs text-white w-6">{month.count}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Spread Distribution */}
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <Zap className="h-5 w-5 text-primary-500" />
            <h3 className="font-semibold text-white">Spread Preferences</h3>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-400">Most used:</span>
              <span className="text-white font-medium">
                {analytics.mostUsedSpread.name} ({analytics.mostUsedSpread.count})
              </span>
            </div>
            
            <div className="space-y-2">
              {analytics.spreadDistribution.slice(0, 4).map((spread, index) => (
                <div key={index} className="flex items-center gap-3">
                  <span className="text-xs text-gray-400 flex-1 truncate">{spread.name}</span>
                  <div className="flex-1 bg-dark-700 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-primary-500 to-purple-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${spread.percentage}%` }}
                    />
                  </div>
                  <span className="text-xs text-white w-8">{spread.percentage}%</span>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Top Tags */}
        {analytics.mostUsedTags.length > 0 && (
          <Card>
            <div className="flex items-center gap-2 mb-4">
              <Star className="h-5 w-5 text-primary-500" />
              <h3 className="font-semibold text-white">Popular Tags</h3>
            </div>
            
            <div className="space-y-2">
              {analytics.mostUsedTags.map((tagData, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm text-gray-300">{tagData.tag}</span>
                  <span className="text-xs text-gray-400 bg-dark-700 px-2 py-1 rounded">
                    {tagData.count}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Reading Insights */}
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <Heart className="h-5 w-5 text-primary-500" />
            <h3 className="font-semibold text-white">Reading Insights</h3>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-400">Favorite rate:</span>
              <span className="text-white font-medium">
                {Math.round((analytics.favoriteReadings / analytics.totalReadings) * 100)}%
              </span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-400">Private readings:</span>
              <span className="text-white font-medium">
                {Math.round((analytics.privateReadings / analytics.totalReadings) * 100)}%
              </span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-400">Completion rate:</span>
              <span className="text-white font-medium">
                {Math.round((analytics.completedReadings / analytics.totalReadings) * 100)}%
              </span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-400">Current streak:</span>
              <span className="text-white font-medium flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {analytics.readingStreak} days
              </span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}